import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { cn } from '../helpers/cn';

export type CinemaDescriptionMarkdownProps = {
    description: string;
    className?: string;
    /** Overrides/extends the default component renderers, e.g. to open YouTube links in a video modal. */
    components?: Partial<Components>;
    /**
     * Renders raw HTML embedded in the markdown instead of printing it as text. Off by default, and
     * deliberately so — see the note on the component below. Turn it on only where the editor
     * feeding this text offers formatting that markdown cannot express, such as underline.
     */
    allowInlineHtml?: boolean;
};

// Cinema descriptions used to be freeform plain text rendered with `white-space: pre-wrap`, so every
// newline (not only blank-line paragraph breaks) was a visible line break. CommonMark treats a single
// newline as a soft break (collapsed to a space), so we upgrade lone newlines to hard breaks (two
// trailing spaces) before parsing. Blank-line paragraph breaks (`\n\n`), as produced by the markdown
// editor, are left untouched. This keeps un-edited plain-text descriptions rendering exactly as before.
function preserveLineBreaksAsHardBreaks(markdown: string): string {
    return markdown.replace(/([^\n])\n(?!\n)/g, '$1  \n');
}

function defaultComponents(): Partial<Components> {
    return {
        // Headings render at distinct sizes so the three levels the editor offers are actually
        // distinguishable on the page. Still <p> rather than real <h*>: this text sits inside
        // pages that already own their heading hierarchy, and operator copy must not inject
        // competing document outline levels.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h1: ({ node, ...props }) => (
            <p
                {...props}
                className="text-headline-20"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h2: ({ node, ...props }) => (
            <p
                {...props}
                className="text-headline-18"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h3: ({ node, ...props }) => (
            <p
                {...props}
                className="text-body-bold-16"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h4: ({ node, ...props }) => (
            <p
                {...props}
                className="text-body-bold-14"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        p: ({ node, ...props }) => (
            <p
                {...props}
                className="text-body-14"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        a: ({ node, href, ...props }) => (
            <a
                {...props}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[var(--secondary-color)]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        strong: ({ node, ...props }) => (
            <strong
                {...props}
                className="font-montserrat-bold"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ul: ({ node, ...props }) => (
            <ul
                {...props}
                className="list-disc pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ol: ({ node, ...props }) => (
            <ol
                {...props}
                className="list-decimal pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        li: ({ node, ...props }) => (
            <li
                {...props}
                className="ml-[16px] text-body-14"
            />
        )
    };
}

/**
 * Renders a cinema's `description` field as basic markdown (bold, italic, headings, lists, links) —
 * the subset produced by the matching MDXEditor configuration.
 *
 * Raw HTML is not rendered unless `allowInlineHtml` is set, and the default stays off on purpose:
 * most callers show plain cinema descriptions that never need it, and enabling it everywhere would
 * widen what operator-entered text can put on the page for all of them at once. Callers whose
 * editor offers underline have to opt in, because markdown has no underline syntax and MDXEditor
 * emits `<u>` for it — without the plugin that tag would show up as literal text.
 */
function CinemaDescriptionMarkdown(props: CinemaDescriptionMarkdownProps): React.JSX.Element | null {
    const { description, className, components, allowInlineHtml } = props;

    if (!description) {
        return null;
    }

    return (
        <div className={cn('space-y-16', className)}>
            <ReactMarkdown
                components={{ ...defaultComponents(), ...components }}
                rehypePlugins={allowInlineHtml ? [rehypeRaw] : undefined}>
                {preserveLineBreaksAsHardBreaks(description)}
            </ReactMarkdown>
        </div>
    );
}

export default CinemaDescriptionMarkdown;
